import type {MenuTreeNode} from '../../../../../app/shared/types/menu-tree'
import type {MenuGroup, MenuItem} from './types'
import { DPR_ROUTE, COASTAL_POSITION_REPORT_ROUTE } from '../../../../../app/modules/operations/core/routes'
import {normalizeBackendLabel, resolveBackendMenuRoute} from './backendMenuRouteMap'
import { filterPermanentlyHiddenStructureNodes } from '../../../../../app/shared/navigation/permanentlyHiddenMenus'

type BuildLevelOpts = {
  /** When Crewing Module backs synthetic Crewingtest, unmatched `/crewingtest/` leaves inherit parent menu match. */
  syntheticCrewingtest?: boolean
}

type StructureNode = {
  key: string
  label: string
  aliases?: string[]
  route?: string
  icon?: string
  children?: StructureNode[]
}

/**
 * **Locked production sidebar.** Order, labels, nesting, and routes mirror **`myMenus.ts`** and **`doctor/docs/FRONTEND_MENU_ROUTE_AUDIT.md`**.
 * **`/api/menus/tree`** only gates **`canView`** (see **`buildMenusFromFrontendStructure`**).
 */
const RAW_FRONTEND_MENU_STRUCTURE: StructureNode[] = [
  {
    key: 'admin',
    label: 'Admin',
    aliases: ['admin'],
    icon: 'admin',
    children: [
      {
        key: 'companies',
        label: 'Companies',
        aliases: ['companies', 'company admin', 'company'],
        route: '/manage/companies',
      },
      {
        key: 'sub-companies',
        label: 'Sub-Companies',
        aliases: ['sub-companies', 'sub companies', 'company group admin', 'company group'],
        route: '/manage/subcompanies',
      },
      {key: 'vessels', label: 'Vessels', aliases: ['vessels'], route: '/manage/vessels'},
      {key: 'user-management', label: 'User Management', aliases: ['user management', 'users'], route: '/manage/usermanagement'},
      {key: 'vendor-management', label: 'Vendor Management', aliases: ['vendor management', 'vendor'], route: '/manage/vendormanagement'},
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    aliases: ['operations'],
    icon: 'Operations',
    children: [
      {key: 'voyage-reports', label: 'Voyage Reports', aliases: ['voyage reports'], route: '/operations/voyages'},
      {key: 'position-reports', label: 'Position Reports', aliases: ['position reports'], route: '/operations/overview'},
      {
        key: 'daily-progress-reports',
        label: 'Daily Progress Reports',
        aliases: ['daily position reports', 'daily progress reports', 'dpr'],
        route: DPR_ROUTE,
      },
      {
        key: 'coastal-position-report',
        label: 'Coastal Position Report',
        aliases: ['coastal position report', 'coastal position reports', 'dmr', 'daily movement report'],
        route: COASTAL_POSITION_REPORT_ROUTE,
      },
      {key: 'cargo-operations', label: 'Cargo Operations', aliases: ['cargo operations'], route: '/operations/cargo-operations'},
      {key: 'library', label: 'Library', aliases: ['library'], route: '/operations/library'},
      {
        key: 'inport-consumption',
        label: 'In-port Consumption Report',
        aliases: ['in-port consumption report', 'inport consumption report', 'in port consumption'],
        route: '/operations/inport-consumption',
      },
      {key: 'offshore-report', label: 'Off Shore Report', aliases: ['off shore report', 'offshore report'], route: '/operations/offshore-report'},
      {key: 'form-index', label: 'Form Index', aliases: ['form index'], route: '/operations/form-index'},
      {
        key: 'reports',
        label: 'Reports',
        aliases: ['reports'],
        route: '/operations/reports/kpis',
        children: [
          {
            key: 'kpis',
            label: 'KPIs',
            aliases: ['kpis', 'kpi', 'assignment'],
            route: '/operations/reports/kpis',
          },
          {
            key: 'voyage-abstract',
            label: 'Voyage Abstract',
            aliases: ['voyage abstract', 'menu'],
            route: '/operations/reports/voyage-abstract',
          },
          {
            key: 'cii-summary',
            label: 'CII Summary',
            aliases: ['cii summary', 'template', 'cii'],
            route: '/operations/reports/cii-summary',
          },
          {
            key: 'cargo-plan-log',
            label: 'Cargo Plan/Log',
            aliases: ['cargo plan log', 'cargo plan', 'submenu', 'field'],
            route: '/operations/reports/cargo-plan-log',
          },
        ],
      },
      {key: 'defects-list', label: 'Defects List', aliases: ['defects list', 'operations defects'], route: '/operations/defects-list'},
    ],
  },
  {
    key: 'crewing',
    label: 'Crewing',
    aliases: ['crewing'],
    icon: 'crewing',
    children: [
      {key: 'crew-dashboard', label: 'Dashboard', aliases: ['dashboard', 'crew dashboard'], route: '/crewing/dashboard'},
      {
        key: 'crew-master',
        label: 'Crew Master',
        aliases: ['crew master', 'crew management', 'crew list', 'crew onboard', 'signing', 'crew documents', 'crew list overview'],
        route: '/crewingtest/crewinglist',
      },
      {key: 'crew-assignment', label: 'Crew Assignment', aliases: ['crew assignment'], route: '/crewing/assignment'},
      {
        key: 'crew-resthours-overtime',
        label: 'Crew Resthours and Overtime',
        aliases: ['crew resthours and overtime', 'crew rest hours and overtime', 'workhours', 'work hours'],
        children: [
          {
            key: 'rest-hour-mgmt',
            label: 'Rest Hour Management',
            aliases: ['rest hour management', 'rest hours management', 'rest hours'],
            route: '/crewingtest/resthours',
          },
          {
            key: 'overtime-entry',
            label: 'Crew Overtime',
            aliases: ['crew overtime', 'overtime entry', 'overtime'],
            route: '/crewing/crewovertime',
          },
        ],
      },
      {key: 'crew-deduction', label: 'Crew Deduction', aliases: ['crew deduction', 'wages portage', 'wages / portage'], route: '/crewing/crewdeductions'},
      {
        key: 'crew-claims',
        label: 'Crew Claims and Reimbursement',
        aliases: ['crew claims and reimbursement', 'claims', 'crew claims'],
        route: '/crewing/crewclaims',
      },
      {
        key: 'crew-allotment',
        label: 'Crew Allotment and Advance',
        aliases: ['crew allotment and advance', 'allotment advances', 'allotment / advances'],
        route: '/crewing/crewallotment',
      },
      {key: 'crew-appraisal', label: 'Crew Appraisal', aliases: ['crew appraisal', 'appraisal'], route: '/crewing/appraisal'},
      {
        key: 'crew-training',
        label: 'Crew Training, Test & Verification',
        aliases: ['crew training test & verification', 'crew training test and verification', 'training'],
        route: '/crewing/crewtraining',
      },
      {key: 'crew-complaints', label: 'Crew Complaints', aliases: ['crew complaints'], route: '/crewing/crewcomplaints'},
    ],
  },
  {
    key: 'technical',
    label: 'Technical',
    aliases: ['technical'],
    icon: 'Technical',
    children: [
      {key: 'machinery', label: 'Machinery', aliases: ['machinery'], route: '/technical/machinery'},
      {
        key: 'pms',
        label: 'PMS',
        aliases: ['pms', 'planned maintenance'],
        route: '/technical/pms/dashboard',
        children: [
          {key: 'pms-dashboard', label: 'Dashboard', aliases: ['dashboard', 'pms dashboard'], route: '/technical/pms/dashboard'},
          {key: 'pms-jobs', label: 'Jobs', aliases: ['jobs', 'pms jobs'], route: '/technical/pms/jobs'},
          {key: 'running-hours', label: 'Running Hours', aliases: ['running hours'], route: '/technical/pms/running-hours'},
          {key: 'calendar-planning', label: 'Calendar Planning', aliases: ['calendar planning'], route: '/technical/pms/calendar-planning'},
        ],
      },
      {key: 'monthly-log', label: 'Monthly Log', aliases: ['monthly log'], route: '/technical/monthly-log'},
      {
        key: 'procurement',
        label: 'Procurement',
        aliases: ['procurement', 'technical procurement'],
        route: '/procurement/overview',
        children: [
          {key: 'po-overview', label: 'Overview', aliases: ['overview', 'procurement overview'], route: '/procurement/overview'},
          {key: 'requisitions', label: 'Requisitions', aliases: ['requisitions'], route: '/procurement/requisition'},
          {key: 'rfq-management', label: 'RFQ Management', aliases: ['rfq management', 'rfq'], route: '/procurement/rfq-management'},
          {
            key: 'quotation-management',
            label: 'Quotation Management',
            aliases: ['quotation management'],
            route: '/procurement/quotation-management',
          },
          {key: 'purchase-orders', label: 'Purchase Orders', aliases: ['purchase orders'], route: '/procurement/purchase-orders'},
          {
            key: 'approval-library',
            label: 'Approval Limits',
            aliases: ['approval library', 'approval limits'],
            route: '/procurement/approval-library',
          },
          {
            key: 'currency-exchange-rates',
            label: 'Currency Exchange Rates',
            aliases: ['currency exchange rates', 'exchange rates', 'currency rates'],
            route: '/procurement/currency-exchange-rates',
          },
          {key: 'company-po-info', label: 'Company PO Info', aliases: ['company po info'], route: '/procurement/company-po-info'},
          {
            key: 'delivery-tracking',
            label: 'Delivery Tracking',
            aliases: ['delivery tracking', 'procurement trail'],
            route: '/procurement/delivery-tracking',
          },
          {key: 'goods-receipt', label: 'Goods Receipt', aliases: ['goods receipt', 'grn'], route: '/procurement/goods-receipt'},
          {key: 'invoice-management', label: 'Invoice Management', aliases: ['invoice management', 'invoices'], route: '/procurement/invoice-management'},
          {key: 'audit-trail', label: 'Audit Trail', aliases: ['audit trail'], route: '/procurement/audit-trail'},
          {key: 'procurement-user-mgmt', label: 'User Management', aliases: ['user management'], route: '/procurement/user-management'},
        ],
      },
      {key: 'technical-defects', label: 'Defects List', aliases: ['defects list', 'technical defects'], route: '/technical/defects-list'},
    ],
  },
  {
    key: 'qhse',
    label: 'QHSE',
    aliases: ['qhse'],
    icon: 'qhse',
    children: [
      {
        key: 'static-templates',
        label: 'Static Templates',
        aliases: ['static templates', 'library', 'forms library', 'forms-checklists/library'],
        route: '/qhse/forms-checklists/library',
      },
      {key: 'document-library', label: 'Document Library', aliases: ['document library'], route: '/qhse/document-library'},
      {key: 'office-certificates', label: 'Office Certificates', aliases: ['office certificates'], route: '/qhse/office-certificates'},
      {key: 'vessel-certificates', label: 'Vessel Certificates', aliases: ['vessel certificates', 'certificates'], route: '/qhse/vessel-certificates'},
      {key: 'manuals-plans', label: 'Manuals and Plans', aliases: ['manuals and plans', 'manuals & plans'], route: '/qhse/manuals-and-plans'},
      {key: 'qhse-defects', label: 'Defects List', aliases: ['defects list', 'qhse defects'], route: '/qhse/defects-list'},
      {
        key: 'forms-checklists',
        label: 'Forms & Checklists',
        aliases: ['forms and checklists', 'forms checklists'],
        route: '/qhse/forms-checklists/submissions',
        children: [
          {key: 'fc-submissions', label: 'Submissions', aliases: ['submissions'], route: '/qhse/forms-checklists/submissions'},
          {
            key: 'work-permits',
            label: 'Work Permits and Checklists',
            aliases: ['work permits and checklists', 'work permits'],
            route: '/qhse/work-permits-and-checklists',
          },
          {key: 'office-certificates', label: 'Office Certificates', aliases: ['office certificates'], route: '/qhse/office-certificates'},
          {key: 'vessel-certificates', label: 'Vessel Certificates', aliases: ['vessel certificates', 'certificates'], route: '/qhse/vessel-certificates'},
          {key: 'manuals-plans', label: 'Manuals and Plans', aliases: ['manuals and plans', 'manuals & plans'], route: '/qhse/manuals-and-plans'},
          {key: 'qhse-defects', label: 'Defects List', aliases: ['defects list', 'qhse defects'], route: '/qhse/defects-list'},
          {
            key: 'near-miss',
            label: 'Near Miss reports',
            aliases: ['near miss reports', 'near miss', 'accident near miss reports'],
            route: '/qhse/accident-near-miss-reports',
          },
          {
            key: 'safety-committee',
            label: 'Safety committee meeting',
            aliases: ['safety committee meeting', 'safety committee'],
            route: '/qhse/safety-committee-meeting',
          },
          {
            key: 'risk-assessment',
            label: 'Risk Assessment Library',
            aliases: ['risk assessment library', 'risk assessment form', 'risk assessment'],
            route: '/qhse/risk-assessment',
          },
        ],
      },
      {
        key: 'audits-and-inspection',
        label: 'Audits and Inspection',
        aliases: ['audits and inspection', 'audits & inspection', 'vessel audits', 'vessel inspection', 'audit'],
        route: '/qhse/audit-index',
        children: [
          {key: 'audit-index', label: 'Audit Index', aliases: ['audit index'], route: '/qhse/audit-index'},
          {key: 'audit-planning', label: 'Audit Planning', aliases: ['audit planning'], route: '/qhse/audit-planning'},
          {key: 'audit-findings', label: 'Audit Findings', aliases: ['audit findings'], route: '/qhse/audit-findings'},
          {key: 'inspection-index', label: 'Inspection Index', aliases: ['inspection index'], route: '/qhse/inspindex'},
          {key: 'inspection-planning', label: 'Inspection Planning', aliases: ['inspection planning'], route: '/qhse/insplanning'},
          {
            key: 'inspection-findings',
            label: 'Inspection Findings',
            aliases: ['inspection findings', 'inspection finding index'],
            route: '/qhse/inspection-findings',
          },
        ],
      },
      {
        key: 'month-end-reports',
        label: 'Month End Reports',
        aliases: ['month end reports', 'month end library', 'months end report', 'main engine consumption', 'machinery status'],
        route: '/qhse/month-end-reports',
      },
      {
        key: 'emergency-drills',
        label: 'Emergency Drills',
        aliases: ['emergency drills'],
        route: '/qhse/emergency-drills/planner',
        children: [
          {key: 'drill-planner', label: 'Drill Planner', aliases: ['drill planner'], route: '/qhse/emergency-drills/planner'},
          {
            key: 'emergency-drill-report',
            label: 'Emergency Drill Report',
            aliases: ['emergency drill report'],
            route: '/qhse/emergency-drills/report',
          },
          {
            key: 'emergency-drill-review',
            label: 'Emergency Drill Review',
            aliases: ['emergency drill review'],
            route: '/qhse/emergency-drills/review',
          },
        ],
      },
    ],
  },
  {
    key: 'accounts',
    label: 'Accounts',
    aliases: ['accounts', 'accounting'],
    icon: 'accounts',
    children: [{key: 'accounts-home', label: 'Accounts', aliases: ['accounts overview', 'overview'], route: '/accounts'}],
  },
]

export const FRONTEND_MENU_STRUCTURE: StructureNode[] = RAW_FRONTEND_MENU_STRUCTURE.map((moduleNode) => ({
  ...moduleNode,
  children: filterPermanentlyHiddenStructureNodes(moduleNode.label, moduleNode.children || []),
}))

const aliases = (node: StructureNode): string[] => [node.label, ...(node.aliases || [])].map(normalizeBackendLabel)

const matchNode = (nodes: MenuTreeNode[] | undefined, node: StructureNode): MenuTreeNode | null => {
  const wanted = new Set(aliases(node))
  const found = (nodes || []).find((n) => wanted.has(normalizeBackendLabel(n.name || '')))
  return found || null
}

const buildLevel = (
  moduleLabel: string,
  nodes: MenuTreeNode[] | undefined,
  config: StructureNode[],
  parentLabel?: string,
  opts?: BuildLevelOpts,
  parentMatched?: MenuTreeNode | null
): {items: MenuItem[]; missingRouteKeys: string[]} => {
  const items: MenuItem[] = []
  const missingRouteKeys: string[] = []

  config.forEach((node) => {
    const matched = matchNode(nodes, node)
    const routeResolved = resolveBackendMenuRoute(moduleLabel, parentLabel ?? node.label, parentLabel ? node.label : null)
    const route = node.route || routeResolved.route || '#'
    if (route === '#') {
      missingRouteKeys.push(routeResolved.keyTried[0] || `${moduleLabel}|${node.label}`)
    }

    if (node.children && node.children.length > 0) {
      // When backend omits an intermediate label (production often groups siblings), fall back to
      // matching structured children against the current menu tier (siblings).
      const childPool =
        matched?.children && matched.children.length > 0 ? matched.children : (nodes ?? [])
      const nested = buildLevel(moduleLabel, childPool, node.children, node.label, opts, matched ?? parentMatched)
      const visible = nested.items.length > 0 || Boolean(matched?.canView)
      if (!visible) return
      items.push({
        name: node.label,
        route,
        icon: 'none',
        svg_location: '',
        submenu: nested.items,
      })
      missingRouteKeys.push(...nested.missingRouteKeys)
      return
    }

    if (
      opts?.syntheticCrewingtest &&
      !matched &&
      parentMatched?.canView &&
      route.startsWith('/crewingtest/') &&
      route !== '#'
    ) {
      items.push({
        name: node.label,
        route,
        icon: 'none',
        svg_location: '',
      })
      return
    }

    if (!matched?.canView) return
    items.push({
      name: node.label,
      route,
      icon: 'none',
      svg_location: '',
    })
  })

  return {items, missingRouteKeys}
}

const countStructuredLeaves = (items: MenuItem[]): number => {
  let total = 0
  for (const item of items) {
    const subs = item.submenu
    if (subs && subs.length > 0) {
      total += countStructuredLeaves(subs)
    } else {
      total += 1
    }
  }
  return total
}

const CREWING_SYNTH_SOURCE_KEY = 'crewingtest'

const canonicalCrewingModule = (moduleList: MenuTreeNode[]): MenuTreeNode | undefined =>
  moduleList.find((m) => m.level === 'MODULE' && normalizeBackendLabel(m.name || '') === 'crewing')

export const buildMenusFromFrontendStructure = (apiTree: MenuTreeNode[]): MenuGroup[] => {
  const modules = (apiTree || []).filter((m) => m.level === 'MODULE')
  const topLevel: MenuItem[] = []
  const missingRouteKeys: string[] = []
  const usedBackendModules = new Set<string>()

  FRONTEND_MENU_STRUCTURE.forEach((moduleCfg) => {
    const matchedBackendModule = matchNode(modules, moduleCfg)
    let resolvedModule: MenuTreeNode | null = matchedBackendModule
    const routeModuleLabel = moduleCfg.label
    let syntheticCrewingtest = false

    if (!resolvedModule && moduleCfg.key === CREWING_SYNTH_SOURCE_KEY) {
      const crewingMod = canonicalCrewingModule(modules)
      if (!crewingMod) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[SidebarMenu] crewingtest synthetic visibility', {
            canonicalCrewingVisible: false,
            matchedLeafCount: 0,
            visibleCrewingtestItems: 0,
            reasonHidden: 'no_canonical_crewing_module_in_tree',
          })
        }
        return
      }
      if (!crewingMod.canView) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('[SidebarMenu] crewingtest synthetic visibility', {
            canonicalCrewingVisible: false,
            matchedLeafCount: 0,
            visibleCrewingtestItems: 0,
            reasonHidden: 'canonical_crewing_hidden',
          })
        }
        return
      }
      resolvedModule = crewingMod
      syntheticCrewingtest = true
    }

    if (matchedBackendModule) {
      usedBackendModules.add(normalizeBackendLabel(matchedBackendModule.name))
    } else if (syntheticCrewingtest && resolvedModule) {
      usedBackendModules.add(normalizeBackendLabel(resolvedModule.name))
    }

    if (!resolvedModule) return

    const children = buildLevel(routeModuleLabel, resolvedModule.children, moduleCfg.children || [], undefined, {
      syntheticCrewingtest,
    })
    let moduleVisible = Boolean(resolvedModule.canView) || children.items.length > 0
    let submenu = children.items

    if (syntheticCrewingtest) {
      moduleVisible = children.items.length > 0
      if (process.env.NODE_ENV === 'development') {
        console.debug('[SidebarMenu] crewingtest synthetic visibility', {
          canonicalCrewingVisible: Boolean(resolvedModule.canView),
          matchedLeafCount: countStructuredLeaves(children.items),
          visibleCrewingtestItems: children.items.length,
          reasonHidden: moduleVisible ? null : 'no_visible_leaves_after_canonical_match',
        })
      }
    }

    if (
      normalizeBackendLabel(moduleCfg.label) === 'accounts' &&
      Boolean(resolvedModule.canView) &&
      children.items.length === 0
    ) {
      moduleVisible = true
      submenu = [
        {
          name: 'Accounts',
          route: '/accounts',
          icon: 'none',
          svg_location: '',
        },
      ]
    }

    if (!moduleVisible) return

    topLevel.push({
      name: moduleCfg.label,
      route: '#',
      icon: moduleCfg.icon || 'none',
      svg_location: 'menu',
      submenu,
    })
    missingRouteKeys.push(...children.missingRouteKeys)
  })

  if (process.env.NODE_ENV === 'development') {
    const orphans = modules.filter(
      (m) => m.canView && !usedBackendModules.has(normalizeBackendLabel(m.name))
    )
    if (orphans.length > 0) {
      console.debug(
        '[SidebarMenu] backend modules with no production layout slot (check alias or add to FRONTEND_MENU_STRUCTURE)',
        orphans.map((m) => m.name)
      )
    }
    if (missingRouteKeys.length > 0) {
      console.debug('[SidebarMenu] missing structured route aliases', Array.from(new Set(missingRouteKeys)))
    }
  }

  return [{name: '', menu: topLevel}]
}

export type LockedSidebarCoverageHiddenEntry = {
  path: string[]
  key?: string
  reason: string
}

export type LockedSidebarCoverageReport = {
  totalLockedNodes: number
  visibleLockedNodes: number
  hiddenLockedNodes: number
  hidden: LockedSidebarCoverageHiddenEntry[]
}

const structureChainByKey = (targetKey: string): StructureNode[] | null => {
  for (const mod of FRONTEND_MENU_STRUCTURE) {
    const walk = (n: StructureNode, ancestors: StructureNode[]): StructureNode[] | null => {
      if (n.key === targetKey) return [mod, ...ancestors, n]
      if (!n.children?.length) return null
      for (const ch of n.children) {
        const hit = walk(ch, [...ancestors, n])
        if (hit) return hit
      }
      return null
    }
    if (mod.key === targetKey) return [mod]
    if (!mod.children?.length) continue
    for (const ch of mod.children) {
      const hit = walk(ch, [])
      if (hit) return hit
    }
  }
  return null
}

/** Every locked-structure node plus synthetic Home row (shown when prepended by `ensureHome`). */
export const collectLockedStructureNodes = (): {path: string[]; key: string}[] => {
  const out: {path: string[]; key: string}[] = [{path: ['Home'], key: 'home'}]
  FRONTEND_MENU_STRUCTURE.forEach((mod) => {
    out.push({path: [mod.label], key: mod.key})
    const walk = (n: StructureNode, ancestors: StructureNode[]) => {
      const chainLabs = [mod.label, ...ancestors.map((a) => a.label), n.label]
      out.push({path: chainLabs, key: n.key})
      n.children?.forEach((ch) => walk(ch, [...ancestors, n]))
    }
    mod.children?.forEach((c) => walk(c, []))
  })
  return out
}

const findBuiltMenuPath = (topLevel: MenuItem[] | undefined, path: string[], depth = 0): boolean => {
  if (!topLevel?.length || depth >= path.length) return false
  const want = normalizeBackendLabel(path[depth])
  const hit = topLevel.find((i) => normalizeBackendLabel(i.name) === want)
  if (!hit) return false
  if (depth === path.length - 1) return true
  return findBuiltMenuPath(hit.submenu, path, depth + 1)
}

const getResolvedBackendModuleForCoverage = (
  apiTree: MenuTreeNode[],
  moduleCfg: StructureNode
): {resolvedModule: MenuTreeNode | null; syntheticCrewingtest: boolean} => {
  const modules = (apiTree || []).filter((m) => m.level === 'MODULE')
  let resolvedModule: MenuTreeNode | null = matchNode(modules, moduleCfg)
  let syntheticCrewingtest = false
  if (!resolvedModule && moduleCfg.key === CREWING_SYNTH_SOURCE_KEY) {
    const crewingMod = canonicalCrewingModule(modules)
    if (!crewingMod?.canView) return {resolvedModule: null, syntheticCrewingtest: false}
    resolvedModule = crewingMod
    syntheticCrewingtest = true
  }
  return {resolvedModule, syntheticCrewingtest}
}

/** Follows the same match / pool rules as `buildLevel` for a single root-to-node chain. */
const traceApiChainVisibility = (apiTree: MenuTreeNode[], chain: StructureNode[]): string => {
  if (chain.length === 0) return 'EMPTY_CHAIN'
  const moduleCfg = chain[0]
  const moduleLabel = moduleCfg.label
  const {resolvedModule, syntheticCrewingtest} = getResolvedBackendModuleForCoverage(apiTree, moduleCfg)
  if (!resolvedModule) return 'BACKEND_MODULE_MISSING_OR_SYNTHETIC_CREWING_UNAVAILABLE'
  if (!resolvedModule.canView && !syntheticCrewingtest) return 'MODULE_CANVIEW_FALSE'
  const opts: BuildLevelOpts = {syntheticCrewingtest}

  if (chain.length === 1) {
    return resolvedModule.canView || syntheticCrewingtest ? 'OK_VISIBLE' : 'MODULE_CANVIEW_FALSE'
  }

  const recurse = (nodes: MenuTreeNode[] | undefined, idx: number, parentMatched: MenuTreeNode | null): string => {
    if (idx >= chain.length) return 'OK_VISIBLE'
    const cfg = chain[idx]

    const matched = matchNode(nodes, cfg)
    const parentLbl = idx > 1 ? chain[idx - 1].label : undefined
    const routeResolved = resolveBackendMenuRoute(moduleLabel, parentLbl ?? cfg.label, parentLbl ? cfg.label : null)
    const route = cfg.route || routeResolved.route || '#'

    if (cfg.children?.length) {
      const childPool =
        matched?.children && matched.children.length > 0 ? matched.children : (nodes ?? [])
      const nested = buildLevel(moduleLabel, childPool, cfg.children, cfg.label, opts, matched ?? parentMatched)
      const visible = nested.items.length > 0 || Boolean(matched?.canView)
      if (!visible) {
        if (!matched) return 'BACKEND_NODE_MISSING_OR_ALIAS (no match for intermediate)'
        if (!matched.canView) return 'INTERMEDIATE_CANVIEW_FALSE_AND_NO_VISIBLE_CHILDREN'
        return 'NO_VISIBLE_CHILDREN_FOR_INTERMEDIATE'
      }
      const next = chain[idx + 1]
      if (!next || !cfg.children.some((c) => c.key === next.key)) return 'STRUCTURE_CHAIN_MISMATCH'
      return recurse(childPool, idx + 1, matched ?? parentMatched)
    }

    if (
      opts.syntheticCrewingtest &&
      !matched &&
      parentMatched?.canView &&
      route.startsWith('/crewingtest/') &&
      route !== '#'
    ) {
      return 'OK_VISIBLE_SYNTHETIC_LEAF_FALLBACK'
    }
    if (!matched) return 'BACKEND_NODE_MISSING_OR_ALIAS_LEAF'
    if (!matched.canView) return 'LEAF_CANVIEW_FALSE'
    return 'OK_VISIBLE'
  }

  return recurse(resolvedModule.children, 1, null)
}

/**
 * Compares the locked `FRONTEND_MENU_STRUCTURE` tree to the **rendered** sidebar menu and explains gaps
 * using the same API matching rules as `buildMenusFromFrontendStructure`.
 *
 * Pass the same `menuTree` and **final** `menuItems[0].menu` you render (after procurement/technical filters).
 */
export const diagnoseLockedSidebarTreeCoverage = (
  apiTree: MenuTreeNode[] | undefined,
  renderedTopLevel: MenuItem[] | undefined
): LockedSidebarCoverageReport => {
  const hidden: LockedSidebarCoverageHiddenEntry[] = []
  const nodes = collectLockedStructureNodes()
  let visibleLockedNodes = 0

  for (const row of nodes) {
    const visible =
      row.key === 'home'
        ? Boolean(renderedTopLevel?.some((i) => i.name === 'Home'))
        : findBuiltMenuPath(renderedTopLevel, row.path)
    if (visible) {
      visibleLockedNodes += 1
      continue
    }

    let reason = 'UNKNOWN_REVIEW_REQUIRED'
    if (row.key === 'home') {
      reason = 'HOME_NOT_IN_RENDERED_MENU'
    } else if (apiTree?.length) {
      const chain = structureChainByKey(row.key)
      if (chain?.length) {
        reason = traceApiChainVisibility(apiTree, chain)
        if (reason.startsWith('OK_VISIBLE')) {
          reason =
            'OK_IN_API_TREE_BUT_MISSING_FROM_RENDERED_MENU (procurement permission filter, technical filter, or sidebar trim)'
        }
      }
    } else {
      reason = 'NO_API_TREE_SUPPLIED_FOR_TRACE'
    }

    hidden.push({path: row.path, key: row.key, reason})
  }

  return {
    totalLockedNodes: nodes.length,
    visibleLockedNodes,
    hiddenLockedNodes: hidden.length,
    hidden,
  }
}
